import { useEffect, useRef } from "react";

/** How loud the microphone is right now, 0 (silence) to 1 (shouting): read
 * once a frame, so the ring follows the voice without re-rendering React. */
export type LevelSource = () => number;

// The strands: a few thin rings drawn over each other, so where they cross
// they glow brighter — the reference's "rings of light". `light` is each
// strand on the dark stage, `ink` the same strand on the light theme's.
const STRANDS = [
  { light: "129, 140, 248", ink: "79, 70, 229", width: 2.4, phase: 0.0, spread: 0.0 },    // indigo, the main ring
  { light: "96, 165, 250", ink: "37, 99, 235", width: 1.6, phase: 1.7, spread: 0.55 },    // blue
  { light: "167, 139, 250", ink: "124, 58, 237", width: 1.4, phase: 3.1, spread: -0.6 },  // violet
  { light: "125, 211, 252", ink: "8, 145, 178", width: 1.0, phase: 4.4, spread: 0.9 },    // cyan
  { light: "99, 102, 241", ink: "67, 56, 202", width: 1.2, phase: 5.6, spread: -1.0 },    // deep indigo
];

// Light added to a light page only makes it whiter, so there the strands are
// ink instead: deeper colours, multiplied, so crossings come out darker rather
// than brighter, with a fainter halo. Read every frame from <html data-theme>,
// so switching the theme switches the ring at once.
const LOOKS = {
  dark: { colour: "light", blend: "lighter", glow: 0.9, blur: 1, inner: "79, 70, 229", innerAlpha: [0.05, 0.18],
          warm: "251, 146, 60", warmAlpha: [0.28, 0.3] },
  light: { colour: "ink", blend: "multiply", glow: 0.4, blur: 0.6, inner: "99, 102, 241", innerAlpha: [0.04, 0.12],
           warm: "234, 88, 12", warmAlpha: [0.22, 0.25] },
} as const;
const POINTS = 120;

/** A ring of light (ink, on the light theme) that breathes while it waits and swells, glows and comes
 * apart into strands while someone speaks. Drawn on a canvas, every frame, from
 * `level()`; `active` false shows the slow breathing only. */
export function VoiceRing({ level, active }: { level: LevelSource; active: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const levelRef = useRef(level);
  const activeRef = useRef(active);
  levelRef.current = level;
  activeRef.current = active;

  useEffect(() => {
    const element = canvas.current;
    const context = element?.getContext("2d");
    if (!element || !context) return;
    let frame = 0;
    let smooth = 0; // the level, eased: quick to rise, slow to fall, like a VU meter
    const started = performance.now();

    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      const ratio = window.devicePixelRatio || 1;
      const { clientWidth: width, clientHeight: height } = element;
      if (element.width !== Math.round(width * ratio) || element.height !== Math.round(height * ratio)) {
        element.width = Math.round(width * ratio);
        element.height = Math.round(height * ratio);
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      const t = (now - started) / 1000;
      const target = activeRef.current ? Math.min(1, Math.max(0, levelRef.current())) : 0;
      smooth += (target - smooth) * (target > smooth ? 0.35 : 0.06);
      const breathe = 0.5 + 0.5 * Math.sin(t * 1.1);
      const cx = width / 2;
      const cy = height / 2;
      const base = Math.min(width, height) * 0.3;
      const radius = base * (1 + 0.04 * breathe + 0.22 * smooth);
      const look = LOOKS[document.documentElement.dataset.theme === "dark" ? "dark" : "light"];

      // A soft glow inside the ring, stronger with the voice.
      const inner = context.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius * 1.15);
      inner.addColorStop(0, `rgba(${look.inner}, 0)`);
      inner.addColorStop(0.75, `rgba(${look.inner}, ${look.innerAlpha[0] + look.innerAlpha[1] * smooth})`);
      inner.addColorStop(1, `rgba(${look.inner}, 0)`);
      context.fillStyle = inner;
      context.fillRect(0, 0, width, height);

      context.globalCompositeOperation = look.blend;
      context.lineJoin = "round";
      for (const [index, strand] of STRANDS.entries()) {
        // Apart a little while waiting, a lot while someone speaks.
        const offset = strand.spread * base * (0.012 + 0.09 * smooth);
        const wobble = base * (0.006 + 0.07 * smooth);
        context.beginPath();
        for (let i = 0; i <= POINTS; i++) {
          const angle = (i / POINTS) * Math.PI * 2;
          const r = radius + offset
            + wobble * Math.sin(3 * angle + t * 1.3 + strand.phase)
            + wobble * 0.6 * Math.sin(5 * angle - t * 0.9 + strand.phase * 2)
            + wobble * 0.3 * Math.sin(2 * angle + t * 2.1 + index);
          const x = cx + r * Math.cos(angle);
          const y = cy + r * Math.sin(angle);
          if (i === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        }
        context.closePath();
        const alpha = index === 0 ? 0.95 : 0.35 + 0.5 * smooth;
        const colour = strand[look.colour];
        context.strokeStyle = `rgba(${colour}, ${alpha})`;
        context.lineWidth = strand.width * (1 + 0.6 * smooth);
        // The glow: only on the first two strands, which is enough and keeps
        // phones smooth.
        context.shadowColor = `rgba(${colour}, ${look.glow})`;
        context.shadowBlur = index < 2 ? (14 + 26 * smooth) * look.blur : 0;
        context.stroke();
      }

      // The warm rim on the inner side, as in the reference: a faint orange
      // arc just inside the ring, on the left.
      context.shadowBlur = 0;
      const warm = context.createLinearGradient(cx - radius, cy, cx + radius * 0.2, cy);
      warm.addColorStop(0, `rgba(${look.warm}, ${look.warmAlpha[0] + look.warmAlpha[1] * smooth})`);
      warm.addColorStop(1, `rgba(${look.warm}, 0)`);
      context.strokeStyle = warm;
      context.lineWidth = 3 + 3 * smooth;
      context.beginPath();
      context.arc(cx, cy, radius * 0.94, Math.PI * 0.55, Math.PI * 1.45);
      context.stroke();
      context.globalCompositeOperation = "source-over";
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  return <canvas ref={canvas} className="voice-ring" aria-hidden="true" />;
}
